import { useState } from "react";
import { X, Trash2, Plus, Minus, ShoppingCart, CheckCircle2, Loader2, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import ProductImage from "@/components/ProductImage";
import { api, formatMoney, formatApiErrorDetail, productImageUrl } from "@/lib/api";

export default function CartDrawer({ open, onClose, onOrderPlaced, endpoint = "/orders", extraPayload = null, headerLabel, successText }) {
  const { items, updateQty, removeItem, clear, total, subtotal, discount, freeItems, lineInfo } = useCart();
  const [note, setNote] = useState("");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const placeOrder = async () => {
    setError("");
    setPlacing(true);
    try {
      await api.post(endpoint, {
        ...(extraPayload || {}),
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty, unit: i.unit })),
        note,
      });
      clear();
      setNote("");
      setSuccess(true);
      onOrderPlaced && onOrderPlaced();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPlacing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
            <ShoppingCart className="h-5 w-5 text-blue-600" /> {headerLabel || "Your Cart"}
          </h3>
          <button data-testid="close-cart" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-green-500" />
            <h4 className="text-lg font-bold text-slate-800">Order placed!</h4>
            <p className="text-sm text-slate-500">{successText || "We've received your order. Track it in the \"Orders\" section."}</p>
            <button
              data-testid="continue-shopping"
              onClick={() => { setSuccess(false); onClose(); }}
              className="mt-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Continue
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-slate-400">
            <ShoppingCart className="h-10 w-10" />
            <p className="text-sm">Your cart is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {items.map((i) => {
                const li = lineInfo(i);
                return (
                <div key={i.key} data-testid={`cart-item-${i.key}`} className="rounded-xl border border-slate-100 p-3">
                  <div className="flex items-center gap-3">
                    <ProductImage path={i.product.image_path} alt={i.product.name} className="h-12 w-12 rounded-lg border border-slate-100" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-800">{i.product.name}</p>
                      <p className="text-xs text-slate-400">
                        {i.product.brand}
                        {i.unitLabel ? <span className="ml-1 rounded bg-slate-100 px-1 text-slate-500">{i.unitLabel}</span> : null}
                      </p>
                      <p className="text-sm font-semibold text-blue-700">{formatMoney(i.rate) || "—"}</p>
                    </div>
                    <div className="flex items-center rounded-lg border border-slate-200">
                      <button onClick={() => updateQty(i.key, i.qty - 1)} className="grid h-7 w-7 place-items-center text-slate-500 hover:text-blue-600">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                      <button onClick={() => updateQty(i.key, i.qty + 1)} className="grid h-7 w-7 place-items-center text-slate-500 hover:text-blue-600">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(i.key)} className="text-slate-300 hover:text-red-500" aria-label="Remove item">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {li.discount > 0 && (
                    <p className="mt-1.5 text-[11px] font-semibold text-green-600" data-testid={`cart-discount-${i.key}`}>
                      🎁 Offer discount: −{formatMoney(li.discount)}
                    </p>
                  )}
                  {li.free && (
                    <p className="mt-0.5 text-[11px] font-semibold text-indigo-600" data-testid={`cart-free-${i.key}`}>
                      + {li.free.qty} {li.free.unit || ""} {li.free.name} — FREE (₹0)
                    </p>
                  )}
                </div>
              );})}

              <textarea
                data-testid="order-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note for your order (optional)"
                rows={2}
                className="w-full resize-none rounded-lg border border-slate-200 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div className="border-t border-slate-100 p-5">
              {error && <p className="mb-2 text-sm font-medium text-red-600">{error}</p>}
              {discount > 0 && (
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Subtotal</span>
                  <span className="text-slate-500">{formatMoney(subtotal)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="mb-1 flex items-center justify-between text-xs" data-testid="cart-total-discount">
                  <span className="font-semibold text-green-600">Offer Discount</span>
                  <span className="font-semibold text-green-600">−{formatMoney(discount)}</span>
                </div>
              )}
              {freeItems.length > 0 && (
                <div className="mb-2 rounded-lg bg-indigo-50 px-2 py-1.5" data-testid="cart-free-summary">
                  {freeItems.map((f, idx) => (
                    <p key={idx} className="text-[11px] font-semibold text-indigo-700">
                      🎁 {f.qty} {f.unit || ""} {f.name} — FREE (₹0)
                    </p>
                  ))}
                </div>
              )}
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-slate-500">Estimated Total</span>
                <span className="text-lg font-bold text-blue-700" data-testid="cart-estimated-total">{formatMoney(total) || "—"}</span>
              </div>
              <button
                data-testid="place-order-button"
                onClick={placeOrder}
                disabled={placing}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-70"
              >
                {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : "Place Order"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
